import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import toast from 'react-hot-toast'
import ExcelJS from 'exceljs'

import { validateFleetDataForExport } from './validateData'
import { generateFleetWorksheet } from './worksheetGenerator'
import { saveWorkbookAsExcel } from '../../../helpers/saveWorkbook'
import { autoSizeColumns } from '../../../helpers/autoSizeColumns'

interface ExportToExcelProps {
  data: Array<Record<string, any>>
  fileName?: string
  titleMap: Record<string, string>
  heading?: string
}

const ExportToExcelButton: React.FC<ExportToExcelProps> = ({
  data,
  fileName = 'ExportedData',
  titleMap,
  heading,
}) => {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (!validateFleetDataForExport(data)) {
      toast.error('No data to export!')
      return
    }

    try {
      setIsExporting(true)
      const workbook = new ExcelJS.Workbook()

      // generate the worksheet using your existing generator
      const worksheet = generateFleetWorksheet(workbook, data, titleMap, heading)

      // Let your auto-size helper run first (it may set reasonable widths)
      autoSizeColumns(worksheet)

      // ----------------------------
      // Post-processing: increase Area/Route width & enable wrapText
      // ----------------------------
      try {
        // Header label for the area/route column (from titleMap)
        const headerLabel = titleMap['zone_street_buildings'] || 'zone_street_buildings'

        // Find header row index by scanning the first few rows (covering cases where a heading was added)
        const maxHeaderScanRows = 5
        let headerRowIndex = -1
        for (let r = 1; r <= maxHeaderScanRows; r++) {
          const row = worksheet.getRow(r)
          let found = false
          row.eachCell((cell) => {
            const cellText = (cell.value ?? '').toString().trim()
            if (cellText === headerLabel) {
              found = true
            }
          })
          if (found) {
            headerRowIndex = r
            break
          }
        }

        // Default to row 1 if not found (safe fallback)
        if (headerRowIndex === -1) headerRowIndex = 1

        // Now locate the column index for the header label
        const headerRow = worksheet.getRow(headerRowIndex)
        let zoneColIndex = -1
        headerRow.eachCell((cell, colNumber) => {
          const cellText = (cell.value ?? '').toString().trim()
          if (cellText === headerLabel) {
            zoneColIndex = colNumber
          }
        })

        if (zoneColIndex > 0) {
          const col = worksheet.getColumn(zoneColIndex)

          // Set a wide column width for the Area/Route column (tweak as needed)
          col.width = 60 // ~characters; adjust (40-80) depending on needs

          // Enable wrapText so '\n' newlines render as multiple lines in Excel
          col.alignment = { wrapText: true, vertical: 'top' }

          // Optionally adjust row heights to accommodate the number of lines
          // Start scanning rows after headerRowIndex (skip the header row(s))
          worksheet.eachRow((row) => {
            if (row.number <= headerRowIndex) return

            const cell = row.getCell(zoneColIndex)
            const text = cell && cell.value ? String(cell.value) : ''
            // Count lines: split on LF
            const lines = text ? text.split('\n').length : 1
            const baseLineHeight = 15 // approximate px per line; tweak if needed
            const calculated = Math.min(400, Math.max(baseLineHeight, baseLineHeight * lines))
            row.height = calculated
          })
        } else {
          // Header not found — non-fatal
          // console.warn('Zone/Area header not found in worksheet; headerLabel=', headerLabel)
        }
      } catch (ex) {
        // Non-fatal; do not block export
        // console.warn('Failed to set Area/Route column width/alignment', ex)
      }

      // Finally save workbook (your helper handles write + save)
      await saveWorkbookAsExcel(workbook, fileName)
      toast.success('Export completed')
    } catch (err) {
      console.error('Export error:', err)
      toast.error('Failed to export data.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button onClick={handleExport} disabled={isExporting} size="sm">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        Download
        {isExporting ? (
          <i className="bi bi-arrow-repeat spinner-border spinner-border-sm" />
        ) : (
          <i className="bi bi-download" />
        )}
      </div>
    </Button>
  )
}

export default ExportToExcelButton
